import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, Check, ChevronRight, Circle, Download, Flame, KeyRound, Radar, ShieldCheck } from 'lucide-react';
import { ErrorState, PageHeader, SeverityBadge, Skeleton, describeError } from '@/components/primitives';
import DailyBars from '@/components/charts/DailyBars';
import WorldMap from '@/components/map/WorldMap';
import { countryName, fmtCount } from '@/lib/geo';
import useResource from '@/pages/dashboard/useResource';
import { apiGet, downloadFile } from '@/pages/dashboard/api';
import { duration, slaWindowLabel } from '@/lib/sla';
import '@/styles/home.css';

const LEVELS = ['low', 'moderate', 'elevated', 'critical'];
const LEVEL_TONE = { low: 'low', moderate: 'medium', elevated: 'high', critical: 'critical' };
const RULES = [
  ['Critical', 'A Critical threat from the last 7 days that nobody has acted on, or an open High or Critical dark web finding.'],
  ['Elevated', 'An open High threat, a known-exploited CVE in an open threat about one incident, or an unacknowledged serious breach.'],
  ['Moderate', 'Only Medium or Low threats are open.'],
  ['Low', 'Everything from the last 7 days has been acknowledged, exported or archived.'],
];
const plural = (n, w, many = `${w}s`) => `${n} ${n === 1 ? w : many}`;
const store = {
  get(k, d) { try { return localStorage.getItem(k) || d; } catch { return d; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch { /* preference for this session only */ } },
};

function Posture({ p }) {
  const idx = LEVELS.indexOf(p.level);
  return (
    <section className="hm-posture" aria-labelledby="hm-posture-h">
      <div className="hm-posture-main">
        <h2 id="hm-posture-h" className="hm-level">{p.level === 'none' ? 'No posture yet' : `Posture is ${p.label.toLowerCase()}`}</h2>
        <div className="hm-meter" role="img" aria-label={idx >= 0 ? `Level ${idx + 1} of 4` : 'No level yet'}>
          {LEVELS.map((l, i) => <span key={l} style={i <= idx ? { background: `var(--state-${LEVEL_TONE[l]})` } : undefined} />)}
        </div>
        <p className="hm-posture-sum">{p.summary}</p>
        <details className="hm-how">
          <summary>How this is decided</summary>
          <dl>{RULES.map(([n, r]) => <div key={n}><dt>{n}</dt><dd>{r}</dd></div>)}</dl>
        </details>
      </div>
      {p.drivers.length > 0 && (
        <ul className="hm-drivers" aria-label="What drives this level">
          {p.drivers.map((d) => (
            <li key={d.key}>
              <Link to={d.to} className="hm-driver" data-tone={d.tone}>
                <b>{d.count}</b>
                <span>{d.label.replace(/^\d+\s+/, '')}</span>
                <ChevronRight aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Stat({ to, label, value, tone, sub, testid }) {
  const body = (
    <>
      <span className="hm-stat-l">{label}</span>
      <span className="hm-stat-v" data-tone={value ? tone : undefined}>{value ?? '-'}</span>
      <span className="hm-stat-s">{sub}</span>
    </>
  );
  return to ? <Link to={to} className="hm-stat" data-testid={testid}>{body}</Link> : <div className="hm-stat" data-testid={testid}>{body}</div>;
}

function AttentionTable({ items }) {
  if (!items.length) {
    return <div className="hm-empty"><Check aria-hidden="true" /><b>Nothing is waiting on you</b><span>Every Critical and High threat from the last 14 days has been acted on.</span></div>;
  }
  return (
    <div className="hm-table-wrap">
      <table className="hm-table">
        <thead><tr><th scope="col">Severity</th><th scope="col">Threat</th><th scope="col" className="hm-hide-md">Owner</th><th scope="col">Due</th><th scope="col" className="hm-num hm-hide-sm">Sources</th></tr></thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.attack_id}>
              <td><SeverityBadge severity={b.severity} size="sm" /></td>
              <td className="hm-title-col">
                <Link to={`/threats?id=${encodeURIComponent(b.attack_id)}`} className="hm-title" title={b.name}>{b.name}</Link>
                {b.kev && <span className="hm-kev"><Flame aria-hidden="true" />Exploited</span>}
              </td>
              <td className="hm-hide-md">{b.owned ? 'You' : <span className="hm-muted">Unassigned</span>}</td>
              <td><span className={`hm-sla hm-sla--${b.sla_state || 'none'}`}>{b.due_at ? (b.sla_state === 'overdue' ? `Overdue ${duration(Date.now() - Date.parse(b.due_at))}` : `Due in ${duration(Date.parse(b.due_at) - Date.now())}`) : 'No target'}</span></td>
              <td className="hm-num hm-hide-sm">{b.source_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Bars({ rows, color = 'var(--accent-from)', empty }) {
  if (!rows.length) return <p className="hm-muted">{empty}</p>;
  // "x of y" rows draw their share of y; plain counts draw against the largest count.
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="hm-bars">
      {rows.map((r) => (
        <li key={r.label}>
          <span className="hm-bars-label" title={r.label}>{r.label}</span>
          <b>{r.value}{r.suffix || ''}</b>
          <span className="hm-bars-track" aria-hidden="true"><i style={{ width: `${Math.max(r.value ? 3 : 0, (100 * r.value) / (r.total || max))}%`, background: r.color || color }} /></span>
        </li>
      ))}
    </ul>
  );
}

function Panel({ title, action, children, className = '' }) {
  return (
    <section className={`hm-panel ${className}`}>
      <div className="hm-panel-h"><h2>{title}</h2>{action}</div>
      <div className="hm-panel-b">{children}</div>
    </section>
  );
}

function Exposure({ exposure, breach, brand }) {
  const watched = (breach.total_domains || 0) + (breach.total_emails || 0) > 0;
  return (
    <div className="hm-expo">
      <Link to="/exposure/dark-web" className="hm-expo-row">
        <Radar aria-hidden="true" />
        <span><b>Dark web</b><span>{exposure.open ? `${plural(exposure.open, 'open finding')} about your organization` : 'No confirmed findings. Monitoring is running.'}</span></span>
        <span className={`hm-pill ${exposure.open ? 'is-bad' : 'is-ok'}`}>{exposure.open ? `${exposure.open} open` : 'Clear'}</span>
      </Link>
      <Link to="/exposure/credentials" className="hm-expo-row">
        <KeyRound aria-hidden="true" />
        <span><b>Credentials</b><span>{!watched ? 'Add a domain to look for exposed employee credentials.' : breach.unacknowledged ? `${plural(breach.unacknowledged, 'breach', 'breaches')} to review${breach.passwords_exposed ? `, ${plural(breach.passwords_exposed, 'password')} exposed` : ''}` : `${plural(breach.total_emails || 0, 'address', 'addresses')} watched, nothing to review`}</span></span>
        {watched && <span className={`hm-pill ${breach.unacknowledged ? 'is-warn' : 'is-ok'}`}>{breach.unacknowledged ? `${breach.unacknowledged} new` : 'Clear'}</span>}
      </Link>
      {brand && (
        <Link to="/exposure/brand" className="hm-expo-row" data-testid="home-brand">
          <ShieldCheck aria-hidden="true" />
          <span><b>Brand protection</b><span>{!brand.set_up ? 'Add your domain to find imitation sites and check your email protection.'
            : brand.active ? `${plural(brand.active, 'lookalike domain')} being handled${brand.high ? `, ${brand.high} high risk` : ''}${brand.email_grade ? `. Email protection ${brand.email_grade}` : ''}`
              : `No live lookalikes${brand.email_grade ? `. Email protection ${brand.email_grade}` : ''}`}</span></span>
          {brand.set_up && <span className={`hm-pill ${brand.high || ['D', 'F'].includes(brand.email_grade) ? 'is-bad' : brand.active || brand.email_grade === 'C' ? 'is-warn' : 'is-ok'}`}>
            {brand.high ? `${brand.high} high` : brand.active ? `${brand.active} open` : 'Clear'}</span>}
        </Link>
      )}
    </div>
  );
}

function Setup({ items }) {
  const left = items.filter((s) => !s.done);
  if (!left.length) return null;
  return (
    <section className="hm-setup" aria-label="Finish setting up">
      <h2>Finish setting up <span className="hm-muted">{items.length - left.length} of {items.length} done</span></h2>
      <ul>
        {items.map((s) => (
          <li key={s.key} data-done={s.done}>
            {s.done ? <Check aria-hidden="true" /> : <Circle aria-hidden="true" />}
            <span><b>{s.label}</b><span>{s.why}</span></span>
            {!s.done && <Link to={s.to} className="is-btn is-btn--secondary is-btn--sm">Set up</Link>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Ransomware leak-site listings in and around the organization's footprint, last 30 days.
 * Public listings, counts only; the full picture is on the Threat map.
 */
function RansomwareNear({ className }) {
  const fp = useResource(() => apiGet('/geo/footprint'), [], { poll: false });
  const all = useResource(() => apiGet('/geo/ransomware', { days: 30, sector: 'all' }), []);
  const mine = useResource(() => apiGet('/geo/ransomware', { days: 30, sector: 'mine' }), []);
  const f = fp.data;
  const area = f ? (f.basis === 'countries' ? f.countries : f.region_countries) : [];
  const where = f?.basis === 'countries' ? 'your countries' : f?.region ? `the ${f.region}` : 'the world';
  const inArea = (d) => (d?.countries || []).filter((c) => !area.length || area.includes(c.iso2));
  const sum = (rows, k = 'count') => rows.reduce((a, c) => a + (c[k] || 0), 0);
  const near = inArea(all.data);
  const nearMine = inArea(mine.data);
  const values = Object.fromEntries((all.data?.countries || []).map((c) => [c.iso2, c.count]));
  const total = sum(near);
  const before = all.data?.comparable ? sum(near, 'previous') : null;
  const failed = (all.error && !all.data) || (fp.error && !fp.data);
  return (
    <Panel title="Ransomware near you" className={className} action={<Link to="/map" className="hm-link">Threat map</Link>}>
      {failed ? <p className="hm-muted">Ransomware activity is unavailable right now.</p> : (
        <div className="hm-near">
          <Link to="/map" className="hm-near-map" aria-label="Open the threat map">
            <WorldMap values={values} footprint={area} fit={area.length ? area : null} compact height={210}
              label={`Map of ransomware listings in ${where}, last 30 days`} />
          </Link>
          <div className="hm-near-facts">
            <div className="hm-near-big">
              <b>{all.data ? fmtCount(total) : '-'}</b>
              <span>organizations in {where} listed on leak sites in the last 30 days{before != null ? `, ${total - before >= 0 ? '+' : ''}${total - before} on the 30 days before` : ''}.</span>
            </div>
            {mine.data?.sector && <p className="hm-note">{fmtCount(sum(nearMine))} of them in {mine.data.sector}, your sector.</p>}
            <Bars rows={near.slice(0, 4).map((c) => ({ label: countryName(c.iso2), value: c.count }))} empty="No listings in this area in the last 30 days." />
          </div>
        </div>
      )}
    </Panel>
  );
}

function Loading() {
  return (
    <div className="hm" aria-busy="true">
      <Skeleton height={190} radius={8} />
      <Skeleton height={92} radius={8} />
      <div className="hm-grid"><div className="hm-c8"><Skeleton height={360} radius={8} /></div><div className="hm-c4"><Skeleton height={360} radius={8} /></div></div>
    </div>
  );
}

/**
 * Where people land after signing in. Analyst view: what needs a decision now
 * and the shape of the last weeks. Executive view: the same facts as a board
 * would ask for them. One request feeds both, so their numbers always agree
 * with each other and with the threat queue.
 */
export default function Home() {
  const navigate = useNavigate();
  const [mode, setModeState] = useState(() => store.get('is.home.mode', 'analyst'));
  const setMode = (m) => { setModeState(m); store.set('is.home.mode', m); };
  const overview = useResource(() => apiGet('/dashboard/overview'), []);
  const o = overview.data;

  const download = async () => {
    try {
      await downloadFile('/dashboard/weekly-report', `IntelliSecure_Weekly_Threat_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Report downloaded');
    } catch (err) {
      toast.error("Couldn't download the report", { description: describeError(err) });
    }
  };

  const header = (
    <PageHeader
      title="Home"
      actions={(
        <>
          <div className="hm-seg" role="group" aria-label="View">
            {[['analyst', 'Analyst'], ['exec', 'Executive']].map(([v, l]) => (
              <button key={v} type="button" aria-pressed={mode === v} onClick={() => setMode(v)} data-testid={`home-${v}`}>{l}</button>
            ))}
          </div>
          <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={download}><Download aria-hidden="true" /> {mode === 'exec' ? 'Board report' : 'Weekly report'}</button>
          {mode === 'analyst' && o?.sla?.overdue > 0 && (
            <Link to="/threats?view=overdue" className="is-btn is-btn--primary is-btn--sm">Work the overdue queue <ArrowRight aria-hidden="true" /></Link>
          )}
        </>
      )}
    />
  );

  if (overview.loading && !o) return <div className="pg">{header}<Loading /></div>;
  if (overview.error && !o) return <div className="pg">{header}<ErrorState title="Couldn't load your overview" error={overview.error} onRetry={overview.reload} /></div>;

  const k = o.kpis; const s = o.sla; const v = o.vulnerabilities; const r = o.response;
  const sevRows = ['Critical', 'High', 'Medium', 'Low'].filter((x) => r.by_severity[x].total);

  return (
    <div className="pg hm" data-testid="home-page">
      {header}
      <Posture p={o.posture} />

      {mode === 'analyst' ? (
        <>
          <section className="hm-stats" aria-label="Key numbers">
            <Stat to="/threats" label="Needs action" value={k.needs_action} tone="critical" sub={`${k.needs_action_critical} Critical, ${k.needs_action_high} High, last 14 days`} testid="stat-needs-action" />
            <Stat to="/threats?view=overdue" label="Overdue against SLA" value={s.overdue} tone="critical" sub={`Critical ${slaWindowLabel(s.hours.Critical)}, High ${slaWindowLabel(s.hours.High)}`} testid="stat-overdue" />
            <Stat to="/threats?view=unassigned" label="Unassigned" value={s.unassigned_open} tone="high" sub="Open threats without an owner" testid="stat-unassigned" />
            <Stat to="/threats" label="New in 24 hours" value={k.new_24h} sub={k.new_prev_24h == null ? 'No comparison yet' : `${k.new_24h - k.new_prev_24h >= 0 ? '+' : ''}${k.new_24h - k.new_prev_24h} on the day before`} />
            <Stat to="/vulnerabilities" label="Known-exploited CVEs" value={k.kev} tone="high" sub={k.kev_total > k.kev ? `${k.kev_total} counting roundup articles` : 'Listed by CISA as exploited'} />
          </section>

          <div className="hm-grid">
            <Panel title="Needs attention" className="hm-c8" action={<Link to="/threats" className="hm-link">Open the queue</Link>}>
              <AttentionTable items={o.attention} />
            </Panel>
            <Panel title="Exposure" className="hm-c4" action={<Link to="/exposure/dark-web" className="hm-link">Details</Link>}>
              <Exposure exposure={o.exposure} breach={o.breach} brand={o.brand} />
            </Panel>

            <Panel title="New threats per day" className="hm-c8" action={<span className="hm-muted hm-hint">Select a day to open its threats</span>}>
              <DailyBars series={o.trend} onSelectDay={(d) => navigate(`/threats?view=all&day=${d}`)} />
            </Panel>
            <Panel title="Exploited vulnerabilities" className="hm-c4" action={<Link to="/vulnerabilities" className="hm-link">All CVEs</Link>}>
              {v.top.length ? (
                <ul className="hm-cves">
                  {v.top.slice(0, 5).map((c) => (
                    <li key={c.cve_id}>
                      <span className="mono">{c.cve_id}</span>
                      <span className="hm-muted hm-cve-what">{[c.vendor, c.product].filter(Boolean).join(' ') || plural(c.briefs, 'threat')}</span>
                      {c.cvss != null && <span className={`hm-pill ${c.cvss >= 9 ? 'is-bad' : 'is-warn'}`}>{c.cvss.toFixed(1)}</span>}
                    </li>
                  ))}
                </ul>
              ) : <p className="hm-muted">No CVEs are named in your threats yet.</p>}
            </Panel>

            <Panel title="Most used techniques" className="hm-c4">
              <Bars rows={o.techniques.slice(0, 5).map((t) => ({ label: t.name, value: t.count }))} empty="No technique data yet." />
            </Panel>
            <Panel title="Attack stages" className="hm-c4" action={<Link to="/attack" className="hm-link">ATT&amp;CK</Link>}>
              <Bars rows={[...o.tactics].sort((a, b) => b.count - a.count).slice(0, 5).map((t) => ({ label: t.name, value: t.count }))} empty="No tactic data yet." />
            </Panel>
            <Panel title="Response, last 30 days" className="hm-c4">
              <Bars rows={sevRows.map((x) => ({ label: x, value: r.by_severity[x].handled, total: r.by_severity[x].total, suffix: ` of ${r.by_severity[x].total}`, color: `var(--state-${x.toLowerCase()})` }))} empty="No threats in the last 30 days." />
              <p className="hm-note">Handled means acknowledged, exported or archived. {s.compliance_pct != null ? `${s.compliance_pct}% met their target.` : `Target compliance appears after 3 handled threats (${s.measured} so far).`}</p>
            </Panel>
            <RansomwareNear className="hm-c12" />
          </div>
          <Setup items={o.setup} />
        </>
      ) : (
        <>
          <section className="hm-stats" aria-label="Board numbers">
            <Stat label="Open Critical risk" value={o.posture.drivers.find((d) => d.key === 'critical')?.count ?? 0} tone="critical" sub="Critical, last 7 days, unresolved" testid="stat-open-critical" />
            <Stat label="Overdue against SLA" value={s.overdue} tone="critical" sub={`Targets: Critical ${slaWindowLabel(s.hours.Critical)}`} />
            <Stat label="SLA compliance" value={s.compliance_pct != null ? `${s.compliance_pct}%` : null} sub={s.compliance_pct != null ? `${s.met} of ${s.measured} handled in time` : `After 3 handled threats (${s.measured} so far)`} />
            <Stat label="Median time to act" value={s.median_hours_to_act != null ? duration(s.median_hours_to_act * 3600e3) : null} sub={s.median_hours_to_act != null ? 'From match to decision' : 'Needs 3 handled threats'} />
            <Stat label="Known-exploited CVEs" value={k.kev} tone="high" sub="In threats about a single incident" />
          </section>
          <div className="hm-grid">
            <Panel title="Top risks to brief the board on" className="hm-c7">
              {o.attention.length ? (
                <ul className="hm-risks">
                  {o.attention.slice(0, 3).map((b) => (
                    <li key={b.attack_id}>
                      <Link to={`/threats?id=${encodeURIComponent(b.attack_id)}`} className="hm-risk">
                        <SeverityBadge severity={b.severity} size="sm" />
                        <span>
                          <b>{b.name}</b>
                          <span className="hm-muted">
                            {[b.reasons.join('. '), b.kev ? 'Listed by CISA as exploited in the wild' : null, b.sla_state === 'overdue' ? 'Past its response target' : null].filter(Boolean).join('. ')}.
                          </span>
                        </span>
                        <ChevronRight aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : <p className="hm-muted">No open Critical or High threats in the last 14 days.</p>}
            </Panel>
            <Panel title="Response performance" className="hm-c5">
              <Bars rows={sevRows.map((x) => ({ label: `${x} handled`, value: r.by_severity[x].handled, total: r.by_severity[x].total, suffix: ` of ${r.by_severity[x].total}`, color: `var(--state-${x.toLowerCase()})` }))} empty="No threats in the last 30 days." />
              <p className="hm-note">Last 30 days. {s.overdue ? `${plural(s.overdue, 'threat')} ${s.overdue === 1 ? 'is' : 'are'} past target now.` : 'Nothing is past its target.'}</p>
              <div className="hm-sub-h">Exposure</div>
              <Exposure exposure={o.exposure} breach={o.breach} brand={o.brand} />
            </Panel>
            <RansomwareNear className="hm-c12" />
          </div>
        </>
      )}
    </div>
  );
}
